/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as RankingLineLayerSpec } from '../../../../layer/preset/line/rankingLine';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface RankingLineLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type RankingLineLayerType = StandardChartLayerSpec &
  RankingLineLayerSpec &
  RankingLineLayerTypeWithFields;

// 创建排名折线图数据类型验证器
const validateRankingLineDataTypes = createDataTypeValidator('rankingLine', [
  { field: 'x', type: ['string', 'date'] },
  { field: 'y', type: 'number' },
  { field: 'z', type: 'string' }
]);

// 创建排名折线图编码验证器
const validateRankingLineEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'z' }
]);

// 额外的检查函数 - 验证数据量是否满足要求
const additionalRankingLineChecks = (layer: RankingLineLayerType, data: Data) => {
  const { encoding } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    utils.logger.warn('warning', `No data found for "${layer.type}" layer`);
    layer.__isEncodingValid = false;
    return;
  }

  // 检查X轴数据点数量是否大于2
  const xValues = new Set(values.map(item => item[encoding.x]));
  if (xValues.size <= 2) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires more than 2 unique x-axis values`
    );
    layer.__isEncodingValid = false;
  }

  // 检查Z轴(分类)数量是否大于3
  const zValues = new Set(values.map(item => item[encoding.z]));
  if (zValues.size <= 3) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires more than 3 unique categories (z-axis values)`
    );
    layer.__isEncodingValid = false;
  }
};

// 创建并导出排名折线图处理器
export const rankingLine = createLayerProcessor<RankingLineLayerType>(
  'rankingLine',
  validateRankingLineEncoding,
  validateRankingLineDataTypes,
  additionalRankingLineChecks
);
